import React from 'react';
import { GlassGradient, GlassPanel, BattenteTriangleFill, AntaRibaltaFill, VasistasFill, FixedMark, BilicoH, BilicoV } from './icons/primitives';

// Import all icon categories
import * as windowIcons from './icons/windows';
import * as frenchDoorIcons from './icons/frenchDoors';
import * as entryDoorIcons from './icons/entryDoors';
import * as slidingIcons from './icons/sliding';
import * as accessoryIcons from './icons/accessories';
import * as shutterIcons from './icons/shutters';
import * as screenIcons from './icons/screens';
import * as openingIcons from './icons/openings';
import * as multiPanelIcons from './icons/multiPanel';

// Merged icon registry
export const WindowIcons = {
  ...windowIcons,
  ...frenchDoorIcons,
  ...entryDoorIcons,
  ...slidingIcons,
  ...accessoryIcons,
  ...shutterIcons,
  ...screenIcons,
  ...openingIcons,
  ...multiPanelIcons,
};

export const ProductIcon = ({ type, className }) => {
  const Icon = WindowIcons[type];
  if (!Icon) {
    return (
      <div className={className}>
        <svg viewBox="0 0 80 100" className="w-full h-full">
          <rect x={10} y={8} width={60} height={84} fill="#BDD2ED" stroke="#333" strokeWidth="1" />
          <rect x={4} y={4} width={72} height={92} fill="none" stroke="#222" strokeWidth="2.5" />
        </svg>
      </div>
    );
  }
  return <div className={className}><Icon /></div>;
};

/**
 * Factory: generates a unified SVG icon with sopraluce/sottoluce INTEGRATED
 * inside the same frame, matching the entry door sopraluce style.
 */
const makeTransomIcon = (iconKey, position = 'top') => {
  let numPanels = 1;
  let baseW = 80;
  const isPf = iconKey.includes('pf');
  if (iconKey.match(/open_2|open_pf2/)) { numPanels = 2; baseW = 100; }
  if (iconKey.match(/open_3|open_pf3/)) { numPanels = 3; baseW = 120; }
  if (iconKey.match(/open_pf4/)) { numPanels = 4; baseW = 140; }

  const pad = 4;
  const inset = 6;
  const transomH = 20;
  const divider = 1;
  const baseH = isPf ? 112 : 92;
  const totalH = baseH + transomH + divider;
  const frameW = baseW - 2 * pad;
  const frameH = totalH - 2 * pad;

  // Inside the frame (pad .. pad+frameH): transom band of transomH, a divider line, then the sashes
  const lineY = position === 'top' ? pad + transomH : pad + frameH - transomH;
  const sopraY = position === 'top' ? pad + 2 : lineY + divider + 2;
  const sopraH = transomH - 4;
  const mainH = frameH - transomH - divider - 4;
  const mainY = position === 'top' ? lineY + divider + 2 : pad + 2;
  const panelW = (frameW - 2 * inset + 4) / numPanels;

  const parseOpenings = (key) => {
    let rest = key.replace(/^open_(pf)?[1-4]_/, '');
    const singleMap = {
      'fisso': 'fisso', 'battente_sx': 'batt_sx', 'battente_dx': 'batt_dx',
      'antaribalta_sx': 'ar_sx', 'antaribalta_dx': 'ar_dx',
      'vasistas': 'vas', 'bilico_h': 'bilico_h', 'bilico_v': 'bilico_v',
    };
    if (singleMap[rest]) return [singleMap[rest]];
    const twoPanel = rest.match(/^(fisso|batt_sx|batt_dx|ar_sx|ar_dx|batt|ar|vas)_(fisso|batt_sx|batt_dx|ar_sx|ar_dx|batt|ar|vas)$/);
    if (twoPanel) return [twoPanel[1], twoPanel[2]];
    const threeMap = {
      'fff': ['fisso','fisso','fisso'],
      'bsf_bd': ['batt_sx','fisso','batt_dx'],
      'ar_f_ar': ['ar_sx','fisso','ar_dx'],
      'f_bs_bd': ['fisso','batt_sx','batt_dx'],
      'bs_bd_f': ['batt_sx','batt_dx','fisso'],
      'ar_ar_f': ['ar_sx','ar_dx','fisso'],
      'f_ar_ar': ['fisso','ar_sx','ar_dx'],
      'bs_bd_bd': ['batt_sx','batt_dx','batt_dx'],
    };
    if (threeMap[rest]) return threeMap[rest];
    const pf3Map = {
      'batt_batt_fisso': ['batt_sx','batt_dx','fisso'],
      'fisso_batt_batt': ['fisso','batt_sx','batt_dx'],
      'batt_fisso_batt': ['batt_sx','fisso','batt_dx'],
      'ar_ar_fisso': ['ar_sx','ar_dx','fisso'],
      'fisso_ar_ar': ['fisso','ar_sx','ar_dx'],
      'ar_fisso_ar': ['ar_sx','fisso','ar_dx'],
    };
    if (pf3Map[rest]) return pf3Map[rest];
    const pf4Map = {
      'batt_batt_batt_batt': ['batt_sx','batt_dx','batt_sx','batt_dx'],
      'ar_ar_ar_ar': ['ar_sx','ar_dx','ar_sx','ar_dx'],
      'fisso_batt_batt_fisso': ['fisso','batt_sx','batt_dx','fisso'],
      'batt_fisso_fisso_batt': ['batt_sx','fisso','fisso','batt_dx'],
    };
    if (pf4Map[rest]) return pf4Map[rest];
    return Array(numPanels).fill('fisso');
  };

  const openings = parseOpenings(iconKey);
  const gradId = `g_tr_${iconKey.replace(/[^a-z0-9]/g, '')}`;

  const renderOpening = (type, px, py, pw, ph, idx) => {
    const key = `op_${idx}`;
    switch(type) {
      case 'batt_sx': case 'batt': return <BattenteTriangleFill key={key} x={px} y={py} w={pw} h={ph} side="left" />;
      case 'batt_dx': return <BattenteTriangleFill key={key} x={px} y={py} w={pw} h={ph} side="right" />;
      case 'ar_sx': case 'ar': return <AntaRibaltaFill key={key} x={px} y={py} w={pw} h={ph} side="left" />;
      case 'ar_dx': return <AntaRibaltaFill key={key} x={px} y={py} w={pw} h={ph} side="right" />;
      case 'vas': return <VasistasFill key={key} x={px} y={py} w={pw} h={ph} />;
      case 'bilico_h': return <BilicoH key={key} x={px} y={py} w={pw} h={ph} />;
      case 'bilico_v': return <BilicoV key={key} x={px} y={py} w={pw} h={ph} />;
      case 'fisso': return <FixedMark key={key} x={px} y={py} w={pw} h={ph} />;
      default: return <FixedMark key={key} x={px} y={py} w={pw} h={ph} />;
    }
  };

  return () => (
    <svg viewBox={`0 0 ${baseW} ${totalH}`} className="w-full h-full">
      <GlassGradient id={gradId} />
      <GlassPanel x={pad + inset} y={sopraY} w={frameW - 2 * inset + 4} h={sopraH} gradId={gradId} />
      <line x1={pad} y1={lineY} x2={pad + frameW} y2={lineY} stroke="#222" strokeWidth="1" />
      {Array.from({length: numPanels}).map((_, i) => (
        <GlassPanel
          key={`gp_${i}`}
          x={pad + inset + i * panelW - (i > 0 ? 1 : 0)}
          y={mainY}
          w={panelW - 2}
          h={mainH}
          gradId={gradId}
        />
      ))}
      {Array.from({length: numPanels - 1}).map((_, i) => (
        <line
          key={`div_${i}`}
          x1={pad + inset + (i + 1) * panelW - 1}
          y1={mainY}
          x2={pad + inset + (i + 1) * panelW - 1}
          y2={mainY + mainH}
          stroke="#333" strokeWidth="0.8"
        />
      ))}
      {openings.map((type, i) => {
        const px = pad + inset + i * panelW;
        return renderOpening(type, px, mainY + 2, panelW - 4, mainH - 4, i);
      })}
      <rect x={pad} y={pad} width={frameW} height={frameH} fill="none" stroke="#222" strokeWidth="2.5" />
    </svg>
  );
};

export const IconWithTransom = ({ type, position = 'top', className }) => {
  const TransomIcon = makeTransomIcon(type, position);
  return <div className={className}><TransomIcon /></div>;
};
